import React, { memo, useEffect, useMemo, useRef, useState } from '../../../../lib/teact/teact';
import { getActions, withGlobal } from '../../../../global';

import type { ApiWalletTransactionCategory } from '../../../../api/types';
import { ContentTab } from '../../../../global/types';

import { requestMeasure } from '../../../../lib/fasterdom/fasterdom';
import { selectCurrentAccountId } from '../../../../global/selectors';
import buildClassName from '../../../../util/buildClassName';

import useHistoryBack from '../../../../hooks/useHistoryBack';
import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';
import useScrolledState from '../../../../hooks/useScrolledState';

import BackHeader from '../../../common/BackHeader';
import MetadataCategoryIcon from '../../../common/MetadataCategoryIcon';
import TabList from '../../../ui/TabList';
import Transition from '../../../ui/Transition';
import Activities from './Activities';

import styles from './ActivityHistory.module.scss';
import contentStyles from './Content.module.scss';

type Props = { isActive?: boolean };

export function ActivityHistory({ isActive }: Props) {
  const lang = useLang();
  const bodyRef = useRef<HTMLDivElement>();
  const categoryTabsRef = useRef<HTMLDivElement>();
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState<ApiWalletTransactionCategory[]>([]);
  const [areCategoryTabsStuck, setAreCategoryTabsStuck] = useState(false);
  const { handleScroll, isScrolled } = useScrolledState();
  const onBack = useLastCallback(() => getActions().setActiveContentTab({ tab: ContentTab.Overview }));
  const handleHeaderClick = useLastCallback(() => {
    const body = bodyRef.current;
    if (!body) return;

    requestMeasure(() => body.scrollTo({ top: 0, behavior: 'smooth' }));
  });
  const handleInactiveCategoryTab = useLastCallback(() => undefined);
  const selectedCategory = useMemo(() => categories.find(({ id }) => id === category), [categories, category]);
  const categoryTabs = useMemo(() => [{
    id: 0,
    title: selectedCategory ? `${selectedCategory.emoji} ${selectedCategory.name}` : lang('All'),
    className: contentStyles.tab,
    isTelemetryBlocked: Boolean(selectedCategory),
    menuClassName: styles.categoryMenu,
    menuItems: categories.length ? [
      { value: '', name: lang('All'), icon: () => <span className={styles.categoryMenuIconPlaceholder} aria-hidden /> },
      ...categories.map((item) => ({
        value: item.id,
        name: item.name,
        isTelemetryBlocked: true,
        icon: () => <MetadataCategoryIcon {...item} />,
      })),
    ] : undefined,
    onMenuItemClick: setCategory,
  }], [categories, lang, selectedCategory]);

  function renderCategoryTabs() {
    return (
      <TabList
        isActive={isActive}
        tabs={categoryTabs}
        activeTab={0}
        className={contentStyles.tabs}
        overlayClassName={buildClassName(
          contentStyles.tabsOverlay,
          selectedCategory && styles.categoryTabsOverlaySelected,
        )}
        onSwitchTab={handleInactiveCategoryTab}
      />
    );
  }

  useEffect(() => {
    const root = bodyRef.current;
    const target = categoryTabsRef.current;
    if (!isActive || !root || !target) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      const isStuck = entry.intersectionRatio < 1 && entry.intersectionRect.bottom > 0;
      setAreCategoryTabsStuck(isStuck);
    }, {
      root,
      rootMargin: '-1px 0px 0px 0px',
      threshold: [1],
    });
    observer.observe(target);

    return () => observer.disconnect();
  }, [isActive]);

  useHistoryBack({ isActive, onBack });
  return (
    <div className={styles.root}>
      <div className={buildClassName(styles.header, areCategoryTabsStuck && styles.headerWithCategoryTabs)}>
        <BackHeader
          title={lang('History')}
          isScrolled={isScrolled}
          onBackClick={onBack}
          onTitleClick={handleHeaderClick}
        />
      </div>
      <div
        ref={bodyRef}
        className={buildClassName(styles.body, 'custom-scroll', 'activity-history-scroll')}
        onScroll={handleScroll}
      >
        <Activities
          isActive={isActive}
          isWidget
          isHistory
          historyCategory={category}
          historyControls={(
            <div
              ref={categoryTabsRef}
              className={buildClassName(
                contentStyles.activityCategoryTabsContext,
                styles.categoryTabsContainer,
              )}
              style={selectedCategory ? `--history-category-color: ${selectedCategory.color}` : undefined}
            >
              <Transition
                name="slideFade"
                className={contentStyles.tabsContent}
                activeKey={0}
                slideClassName={contentStyles.tabsSlide}
                shouldCleanup
                cleanupExceptionKey={0}
              >
                {renderCategoryTabs()}
              </Transition>
            </div>
          )}
          scrollContainerSelector=".activity-history-scroll"
          onHistoryCategoryChange={setCategory}
          onHistoryCategoriesChange={setCategories}
        />
      </div>
    </div>
  );
}

export default memo(withGlobal<Props>(
  (global) => ({ accountId: selectCurrentAccountId(global) }),
)(({ accountId, ...props }) => <ActivityHistory key={accountId} {...props} />));
